import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Download, Chrome, CheckCircle, Settings, Shield, 
  Eye, DollarSign, Bell, HelpCircle, ExternalLink
} from 'lucide-react';

export default function DocsShopperGuide() {
  const supportedRetailers = [
    'Amazon', 'Walmart', 'Target', 'Best Buy', 'Costco',
    'Home Depot', 'Lowes', 'Nike', 'Nordstrom', 'Macys',
    'Kohls', 'Gap', 'Lululemon', 'REI', 'Ulta', 'Sephora',
    'Apple', 'Google Shopping', 'PriceGrabber'
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          <Chrome className="w-3 h-3 mr-1" />
          Chrome Extension
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Commerce Index Shopper Guide
        </h1>
        <p className="text-[#94A3B8] text-lg">
          Shop with better signals, not just better prices. Get price comparisons and live social proof across 25+ retailers.
        </p>
      </div>

      {/* Download Section */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-[#3EF1C7]" />
          Download & Install
        </h2>
        
        <Card className="bg-gradient-to-r from-[#0D1628] to-[#1E293B] border-[#3EF1C7]/30 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-[#3EF1C7]/20 rounded-2xl flex items-center justify-center">
                  <Chrome className="w-10 h-10 text-[#3EF1C7]" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-white text-xl font-semibold mb-2">Commerce Index Shopper</h3>
                <p className="text-[#94A3B8] mb-4">Free Chrome extension for smarter shopping</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button 
                    className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90"
                    onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
                  >
                    <Chrome className="w-4 h-4 mr-2" />
                    Add to Chrome - Free
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B]"
                    onClick={() => window.open('/extension', '_blank')}
                  >
                    Learn More
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Installation Steps */}
        <div className="space-y-4">
          <h3 className="text-white font-medium">Installation Steps</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="pt-6 text-center">
                <div className="w-10 h-10 bg-[#3EF1C7]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#3EF1C7] font-bold">1</span>
                </div>
                <h4 className="text-white font-medium mb-2">Visit Chrome Web Store</h4>
                <p className="text-[#94A3B8] text-sm">Search &ldquo;Commerce Index Shopper&rdquo; or use the button above</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="pt-6 text-center">
                <div className="w-10 h-10 bg-[#3EF1C7]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#3EF1C7] font-bold">2</span>
                </div>
                <h4 className="text-white font-medium mb-2">Click &ldquo;Add to Chrome&rdquo;</h4>
                <p className="text-[#94A3B8] text-sm">Confirm by clicking &ldquo;Add extension&rdquo; in the popup</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="pt-6 text-center">
                <div className="w-10 h-10 bg-[#3EF1C7]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-[#3EF1C7] font-bold">3</span>
                </div>
                <h4 className="text-white font-medium mb-2">Pin the Extension</h4>
                <p className="text-[#94A3B8] text-sm">Click the puzzle icon and pin CI Shopper for easy access</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">Features</h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <DollarSign className="w-8 h-8 text-[#3EF1C7] mb-3" />
              <h4 className="text-white font-medium mb-2">Smart Price Comparison</h4>
              <p className="text-[#94A3B8] text-sm">
                Automatically see prices from other retailers when you view a product. Find the best deal without opening multiple tabs.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Eye className="w-8 h-8 text-[#7A5CFF] mb-3" />
              <h4 className="text-white font-medium mb-2">Live Social Proof</h4>
              <p className="text-[#94A3B8] text-sm">
                For integrated brands, see real-time views and purchases. Know if a product is trending before you buy.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Bell className="w-8 h-8 text-[#FFD166] mb-3" />
              <h4 className="text-white font-medium mb-2">Savings Alerts</h4>
              <p className="text-[#94A3B8] text-sm">
                Get notified when you are about to overpay or when a better price is available elsewhere.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Shield className="w-8 h-8 text-[#3EF1C7] mb-3" />
              <h4 className="text-white font-medium mb-2">Privacy-First</h4>
              <p className="text-[#94A3B8] text-sm">
                No personal tracking. We only look at product pages, never your personal data or browsing history.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Supported Retailers */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">Supported Retailers</h2>
        
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <p className="text-[#94A3B8] mb-4">
              CI Shopper works on 25+ major retail sites:
            </p>
            <div className="flex flex-wrap gap-2">
              {supportedRetailers.map((retailer, i) => (
                <Badge key={i} variant="outline" className="border-[#334155] text-[#94A3B8]">
                  {retailer}
                </Badge>
              ))}
            </div>
            <p className="text-[#64748B] text-sm mt-4">
              More retailers added regularly. Request a retailer at support@commerceindex.com
            </p>
          </CardContent>
        </Card>
      </section>

      {/* How to Use */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">How to Use</h2>
        
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#3EF1C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0B1120] font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Shop as Usual</h4>
                  <p className="text-[#94A3B8] text-sm">Visit any supported retailer and browse products normally</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#3EF1C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0B1120] font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">View Price Comparisons</h4>
                  <p className="text-[#94A3B8] text-sm">CI Shopper automatically shows a widget with prices from other retailers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#3EF1C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0B1120] font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Check Social Proof</h4>
                  <p className="text-[#94A3B8] text-sm">For integrated brands, see how many people are viewing and buying</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#3EF1C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0B1120] font-bold">4</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Click to Save</h4>
                  <p className="text-[#94A3B8] text-sm">Click any price to go directly to that retailer and complete your purchase</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Settings */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#94A3B8]" />
          Extension Settings
        </h2>
        
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <p className="text-[#94A3B8] mb-4">
              Access settings by clicking the CI Shopper icon in your toolbar, then the gear icon.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-[#22304A]">
                <span className="text-[#E2E8F0]">Display Mode</span>
                <span className="text-[#94A3B8] text-sm">Floating, Side Panel, or Minimized</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[#22304A]">
                <span className="text-[#E2E8F0]">Theme</span>
                <span className="text-[#94A3B8] text-sm">Light, Dark, or Auto</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-[#22304A]">
                <span className="text-[#E2E8F0]">Minimum Savings</span>
                <span className="text-[#94A3B8] text-sm">$1 - $100 threshold</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-[#E2E8F0]">Notifications</span>
                <span className="text-[#94A3B8] text-sm">On/Off for savings alerts</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Privacy */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#3EF1C7]" />
          Privacy & Security
        </h2>
        
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-[#3EF1C7] font-medium mb-3">What We Collect</h4>
                <ul className="text-[#94A3B8] text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    Product URLs on supported retailers
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    Anonymous session ID for stats
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    Your settings preferences (locally)
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-[#FF6B6B] font-medium mb-3">What We Do NOT Collect</h4>
                <ul className="text-[#94A3B8] text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF6B6B]">✕</span>
                    Personal information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF6B6B]">✕</span>
                    Payment information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF6B6B]">✕</span>
                    Browsing history outside product pages
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#FF6B6B]">✕</span>
                    Login credentials
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-[#FFD166]" />
          FAQ
        </h2>
        
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <h4 className="text-white font-medium mb-2">Is CI Shopper free?</h4>
              <p className="text-[#94A3B8] text-sm">Yes, CI Shopper is completely free. No account or registration required.</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <h4 className="text-white font-medium mb-2">How accurate are the prices?</h4>
              <p className="text-[#94A3B8] text-sm">Prices are updated multiple times daily. Always verify on the retailer site before purchasing.</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <h4 className="text-white font-medium mb-2">Does it work on mobile?</h4>
              <p className="text-[#94A3B8] text-sm">Chrome extensions do not work on mobile browsers. We are exploring a mobile app.</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <h4 className="text-white font-medium mb-2">Widget not showing?</h4>
              <p className="text-[#94A3B8] text-sm">Try refreshing the page. Make sure you are on a product page (not search results). Check that the extension is enabled at chrome://extensions.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Support */}
      <section className="mb-8">
        <Card className="bg-[#7A5CFF]/10 border-[#7A5CFF]/30">
          <CardContent className="pt-6">
            <h3 className="text-white font-semibold mb-2">Need Help?</h3>
            <p className="text-[#94A3B8] text-sm mb-4">
              Contact us at support@commerceindex.com or visit our support page.
            </p>
            <Button variant="outline" className="border-[#7A5CFF] text-[#7A5CFF] hover:bg-[#7A5CFF]/20">
              <a href="/contact">Contact Support</a>
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
